import dayjs from 'dayjs';
import { DiversionManagerGateCountGroup, DiversionManagerGateCounts } from '../../airports/airport';
import { DateTimeFormatter } from '../../formatters';
import {
    DiversionManagerAirlineEnum,
    DiversionManagerCapacityFieldEnum,
    DiversionManagerNetworkEnum,
    DiversionManagerOtherEnum,
    DiversionPriorityTypeEnum
} from './types';

export interface DiversionSettings {
    terminology: {
        airline: DiversionManagerAirlineEnum;
        network: DiversionManagerNetworkEnum;
        other: DiversionManagerOtherEnum;
    };
    capacity: {
        airline: DiversionManagerCapacityFieldEnum;
        network: DiversionManagerCapacityFieldEnum;
        other: DiversionManagerCapacityFieldEnum;
    };
    priorityType: DiversionPriorityTypeEnum;
}

// ---- Capacity / Terminology ----
export const getTerminologyText = (type: 'Airline' | 'Network' | 'Other', settings: DiversionSettings): string => {
    switch (type) {
        case 'Airline':
            return settings.terminology.airline === DiversionManagerAirlineEnum.major ? 'Major' : 'Airline';
        case 'Network':
            return settings.terminology.network === DiversionManagerNetworkEnum.subCarrier ? 'Sub-Carrier' : 'Network';
        case 'Other':
            return settings.terminology.other === DiversionManagerOtherEnum.oal ? 'OAL' : 'Other';
        default:
            return type;
    }
};

export const getCapacityHeaderText = (type: 'Airline' | 'Network' | 'Other', settings: DiversionSettings): string => {
    const terminology = getTerminologyText(type, settings);
    const capacityType = settings.capacity[type.toLowerCase() as 'airline' | 'network' | 'other'];

    switch (capacityType) {
        case DiversionManagerCapacityFieldEnum.total:
            return `${terminology} (Total)`;
        case DiversionManagerCapacityFieldEnum.narrow:
            return `${terminology} (Narrow)`;
        case DiversionManagerCapacityFieldEnum.wideNarrow:
            return `${terminology} (Narr/Wide)`;
        default:
            return terminology;
    }
};

export const formatCapacityValueByType = (
    narrow: number | null,
    wide: number | null,
    total: number | null,
    capacityType: DiversionManagerCapacityFieldEnum,
    missingValueChar = '-'
): string => {
    switch (capacityType) {
        case DiversionManagerCapacityFieldEnum.total:
            return total !== null ? total.toString() : missingValueChar;
        case DiversionManagerCapacityFieldEnum.narrow:
            return narrow !== null ? narrow.toString() : missingValueChar;
        case DiversionManagerCapacityFieldEnum.wideNarrow:
            if (narrow === null && wide === null) {
                return `${missingValueChar}/${missingValueChar}`;
            }
            if (narrow !== null && wide !== null) {
                return `${narrow}/${wide}`;
            }
            if (narrow !== null) {
                return `${narrow}/${missingValueChar}`;
            }
            if (wide !== null) {
                return `${missingValueChar}/${wide}`;
            }
            return '';
        default:
            return '';
    }
};

export const getCapacityValue = (capacity: DiversionManagerGateCounts, capacityType: DiversionManagerCapacityFieldEnum): number => {
    switch (capacityType) {
        case DiversionManagerCapacityFieldEnum.total:
        case DiversionManagerCapacityFieldEnum.wideNarrow:
            return capacity.total ?? 0;
        case DiversionManagerCapacityFieldEnum.narrow:
            return capacity.narrow ?? 0;
    }
};

export const formatTotalCapacity = (capacities: DiversionManagerGateCountGroup, settings: DiversionSettings): string => {
    const airlineCapacity = getCapacityValue(capacities.airline, settings.capacity.airline);
    const networkCapacity = getCapacityValue(capacities.network, settings.capacity.network);
    const otherCapacity = getCapacityValue(capacities.other, settings.capacity.other);
    const totalCapacity = airlineCapacity + networkCapacity + otherCapacity;
    return totalCapacity ? totalCapacity.toString() : '-';
};

// ---- Planes / Status ----
export const formatStatus = (status: number, subStatus: number): string => {
    if (status === 0) {
        switch (subStatus) {
            case 1:
                return 'ON';
            case 2:
                return 'IN';
            case 0:
            default:
                return 'Enroute';
        }
    } else {
        switch (subStatus) {
            case 2:
                return 'IN';
            case 3:
                return 'OUT';
            case 0:
            default:
                return 'On Ground';
        }
    }
};

export const formatTime = (time: number | null): string => {
    if (time === null) {
        return '';
    }
    const timeDiff = dayjs.utc().diff(dayjs.unix(time), 'seconds');
    const sign = timeDiff >= 0 ? '+' : '-';
    return `${DateTimeFormatter.formatTime(time)} (${sign}${DateTimeFormatter.formatDurationWithoutSeconds(Math.abs(timeDiff))})`;
};

export const formatTimeOnboardAircraft = (time: number | null): string => {
    if (time === null) {
        return '';
    }
    const timeDiff = dayjs.utc().diff(dayjs.unix(time), 'seconds');
    if (timeDiff < 0) {
        return '';
    }
    return DateTimeFormatter.formatDurationWithoutSeconds(timeDiff);
};

export const formatPriority = (priority: number, type: DiversionPriorityTypeEnum): string => {
    if (type === DiversionPriorityTypeEnum.custom) {
        return priority.toString();
    }

    switch (priority) {
        case 0:
            return 'None';
        case 8:
            return 'Yes';
        case 15:
            return 'High';
        default:
            return 'Invalid';
    }
};
